import type { Chat, ChatAttachment } from '@src/api/shared/community/chatApiClient';
import { useReactionContext } from '@src/context/ReactionContext';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import type { LinkPreviewData } from '@src/features/shared/community/components/chat/PreviewOgp';
import { useIsMobile } from '@src/hooks/useIsMobile';
import useTooltip from '@src/hooks/useTooltip';
import { generateHtml, isHtmlEmpty } from '@src/utils/chat';
import { Attachment } from '@src/utils/idb';
import { transformMentionsInHtml } from '@src/utils/mentionUtils';
import React from 'react';
import { useLongPress } from 'use-long-press';

type UseChatMessageDisplayParams = {
  chat: Chat;
  isLoggedIn: boolean;
  currentUserId?: number;
  currentUserType?: ChannelMemberUserTypeEnum;
  linkPreviewCache: Record<string, LinkPreviewData | null>;
  onSetCurrentChatIdSp: (chatId: number) => void;
  onPinnedChatLongPress: () => void;
};

export const useChatMessageDisplay = ({
  chat,
  isLoggedIn,
  currentUserId,
  currentUserType,
  linkPreviewCache,
  onSetCurrentChatIdSp,
  onPinnedChatLongPress,
}: UseChatMessageDisplayParams) => {
  const { reactions: optimisticReactions } = useReactionContext();
  const { showTooltip, displayTooltip } = useTooltip();
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const [mentionClickedChatId, setMentionClickedChatId] = React.useState<number | null>(null);
  const isMobile = useIsMobile();

  const longPressBind = useLongPress(
    (_, { context }) => {
      if (isMobile) {
        onSetCurrentChatIdSp(context as number);
        onPinnedChatLongPress();
      }
    },
    { threshold: 1000, cancelOnMovement: 10 },
  );

  const preview = linkPreviewCache[String(chat.id)];

  const attachments: Attachment[] = React.useMemo(
    () =>
      (chat.attachments ?? []).map((chatAttachment: ChatAttachment) => ({
        name: chatAttachment.file_name,
        type: chatAttachment.extension,
        size: 0,
        blob: new Blob(),
        chatAttachment: chatAttachment,
      })),
    [chat.attachments],
  );

  const htmlWithMentions = React.useMemo(() => {
    const rawHtml = generateHtml(chat.message, !!chat.is_edited);
    return transformMentionsInHtml(rawHtml, isLoggedIn, currentUserId, currentUserType, chat.id);
  }, [chat.message, chat.is_edited, chat.id, isLoggedIn, currentUserId, currentUserType]);

  const reactions = optimisticReactions[chat.id] || chat?.reactions;

  return {
    // State
    mentionClickedChatId,
    setMentionClickedChatId,
    isMobile,

    // Refs
    tooltipRef,

    // Tooltip
    showTooltip,
    displayTooltip,

    // Computed values
    preview,
    attachments,
    htmlWithMentions,
    reactions,

    // Handlers
    longPressBind,

    // Utils
    isHtmlEmpty,
  };
};
